import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Send, 
  Download,
  Calendar,
  CheckCircle,
  Building,
  User,
  MessageSquare,
  Sparkles,
  Star,
  Heart,
  Zap
} from 'lucide-react';
import { mockData } from './mock';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    service: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [contactRef, contactVisible] = useScrollAnimation({ delay: 200 });
  const [formRef, formVisible] = useScrollAnimation({ delay: 400 });
  const [actionsRef, actionsVisible] = useScrollAnimation({ delay: 600 });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        console.log('Form submitted successfully:', result);
        setIsSubmitted(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          organization: '',
          phone: '',
          service: '',
          message: ''
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        console.error('Form submission error:', result.error);
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = () => {
    // Mock brochure download
    alert('Brochure download will be available soon. Please contact us for detailed information.');
  };

  const handleDemo = () => {
    // Mock demo request
    alert('Demo request submitted! We will contact you within 24 hours to schedule your personalized demonstration.');
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-green-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Section Header */}
        <div ref={headerRef} className={`text-center mb-12 sm:mb-16 lg:mb-20 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 border border-green-200/50 backdrop-blur-sm">
            <Sparkles size={14} className="sm:w-4 sm:h-4 text-green-600" />
            <Badge className="bg-transparent text-green-700 px-0 py-0 text-xs sm:text-sm font-semibold">
              Get In Touch
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Ready to Transform
            <span 
              className="block bg-clip-text text-transparent transition-all duration-300 hover:scale-105"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))' 
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundImage = 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundImage = 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))';
              }}
            >
              Your City?
            </span>
          </h2>
          <p className="text-sm sm:text-base lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
            Contact our experts today to discuss how our innovative traffic technology 
            solutions can enhance your urban infrastructure and create smarter cities.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
          {/* Enhanced Contact Information */}
          <div ref={contactRef} className={`lg:col-span-1 ${createScrollAnimationClasses('fadeInLeft', contactVisible)}`}>
            <Card className="shadow-xl border-0 mb-6 sm:mb-8 bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <Heart size={16} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                    Contact Information
                  </CardTitle>
                </div>
                <p className="text-gray-600 text-xs sm:text-sm">Get in touch with our expert team</p>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
                <div className="group flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl hover:bg-blue-50/50 transition-all duration-300">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <MapPin size={16} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors text-sm sm:text-base">Office Location</h4>
                    <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">
                      Traffic Service International<br />
                      Building No: 915, office No: 579<br />
                      CBD (Abu Dhabi bank) Al Falaj street,<br />
                      Muscat, Sultanate of Oman<br />
                      Postal Code: 111
                    </p>
                  </div>
                </div>

                <div className="group flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl hover:bg-green-50/50 transition-all duration-300">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Phone size={16} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-green-600 transition-colors text-sm sm:text-base">Contact Numbers</h4>
                    <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">
                      TEL: {mockData.contact.phone}<br />
                      Mob: {mockData.contact.mobile}
                    </p>
                  </div>
                </div>

                <div className="group flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl hover:bg-purple-50/50 transition-all duration-300">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Mail size={16} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors text-sm sm:text-base">Email Address</h4>
                    <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">{mockData.contact.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Quick Actions */}
            <div ref={actionsRef} className={`space-y-3 sm:space-y-4 ${createScrollAnimationClasses('fadeInUp', actionsVisible)}`}>
              <Button 
                onClick={handleDemo}
                size="lg"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                <Calendar size={16} className="sm:w-5 sm:h-5 mr-2" />
                Request Live Demo
              </Button>
              <Button 
                onClick={handleDownload}
                variant="outline" 
                size="lg"
                className="w-full border-2 border-gray-300 hover:border-green-600 hover:text-green-600 hover:bg-green-50 py-3 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-105 text-sm sm:text-base"
              >
                <Download size={16} className="sm:w-5 sm:h-5 mr-2" />
                Download Brochure
              </Button>
            </div>
          </div>

          {/* Enhanced Contact Form */}
          <div ref={formRef} className={`lg:col-span-2 ${createScrollAnimationClasses('fadeInRight', formVisible)}`}>
            <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <MessageSquare size={16} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                    Send Us a Message
                  </CardTitle>
                </div>
                <p className="text-gray-600 text-xs sm:text-sm">
                  Fill out the form below and our team will get back to you within 24 hours.
                </p>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {isSubmitted ? (
                  <div className="text-center py-8 sm:py-12 lg:py-16">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                      <CheckCircle size={24} className="sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Message Sent Successfully!</h3>
                    <p className="text-gray-600 text-sm sm:text-base lg:text-lg">We'll get back to you within 24 hours.</p>
                    <div className="mt-4 sm:mt-6 flex items-center justify-center gap-2 text-green-600">
                      <Star size={14} className="sm:w-4 sm:h-4 fill-current" />
                      <span className="text-xs sm:text-sm font-semibold">Thank you for your interest!</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="relative group">
                        <User size={16} className="sm:w-5 sm:h-5 absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <Input
                          type="text"
                          name="name"
                          placeholder="Full Name *"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="pl-10 sm:pl-12 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-blue-500 transition-all duration-300 hover:border-gray-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        />
                      </div>
                      <div className="relative group">
                        <Mail size={16} className="sm:w-5 sm:h-5 absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <Input
                          type="email"
                          name="email"
                          placeholder="Email Address *"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="pl-10 sm:pl-12 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-blue-500 transition-all duration-300 hover:border-gray-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="relative group">
                        <Building size={16} className="sm:w-5 sm:h-5 absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <Input
                          type="text"
                          name="organization"
                          placeholder="Organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          className="pl-10 sm:pl-12 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-blue-500 transition-all duration-300 hover:border-gray-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        />
                      </div>
                      <div className="relative group">
                        <Phone size={16} className="sm:w-5 sm:h-5 absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <Input
                          type="tel"
                          name="phone"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="pl-10 sm:pl-12 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-blue-500 transition-all duration-300 hover:border-gray-300 bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    <div className="relative group">
                      <Zap size={16} className="sm:w-5 sm:h-5 absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full py-3 sm:py-4 px-10 sm:px-12 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-blue-500 transition-all duration-300 hover:border-gray-300 bg-white/80 backdrop-blur-sm appearance-none text-sm sm:text-base"
                      >
                        <option value="">Select Service of Interest</option>
                        <option value="smart-lighting">Smart Street Lighting</option>
                        <option value="traffic-enforcement">Traffic Enforcement Systems</option>
                        <option value="monitoring">City Monitoring Systems</option>
                        <option value="solar">Solar Solutions</option>
                        <option value="facade">Facade Lighting</option>
                        <option value="energy-storage">Energy Storage Systems</option>
                        <option value="ai-traffic">AI Traffic Intelligence</option>
                        <option value="consultation">General Consultation</option>
                      </select>
                    </div>

                    <div className="relative group">
                      <MessageSquare size={16} className="sm:w-5 sm:h-5 absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                      <Textarea
                        name="message"
                        placeholder="Tell us about your project requirements..."
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="pl-10 sm:pl-12 pt-3 sm:pt-4 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:border-blue-500 transition-all duration-300 hover:border-gray-300 resize-none bg-white/80 backdrop-blur-sm text-sm sm:text-base"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} className="sm:w-5 sm:h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};